import { getCtx } from "@/lib/sfx";

let voiceOut = null;
let current = null;
let fallbackMode = "device";
let decodeCtx = null;

export const setVoiceFallback = (m) => { fallbackMode = m === "none" ? "none" : "device"; };

export class VoiceError extends Error {
  constructor(reason) {
    super(reason);
    this.reason = reason;
  }
}

const decoder = () => {
  const ctx = getCtx();
  if (ctx) return ctx;
  decodeCtx = decodeCtx || new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 1, 44100);
  return decodeCtx;
};

// Downloads a full (small) mp3 and decodes it; one retry, per-attempt timeout.
export const fetchVoice = async (url, { timeout = 4500, retries = 1 } = {}) => {
  let last = new VoiceError("error");
  for (let i = 0; i <= retries; i++) {
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), timeout);
    try {
      const r = await fetch(url, { signal: ac.signal, cache: "no-store", priority: "high" });
      if (!r.ok) {
        let detail = "";
        try { detail = (await r.json()).detail; } catch (e) { /* not json */ }
        throw new VoiceError(detail || `server ${r.status}`);
      }
      const ab = await r.arrayBuffer();
      try {
        return await decoder().decodeAudioData(ab);
      } catch (e) {
        throw new VoiceError("bad audio");
      }
    } catch (e) {
      if (e instanceof VoiceError) last = e;
      else if (e.name === "AbortError") last = new VoiceError("timeout");
      else last = new VoiceError(navigator.onLine === false ? "no connection" : "network error");
      if (last.reason.startsWith("ElevenLabs")) break;
    } finally {
      clearTimeout(t);
    }
  }
  throw last;
};

const output = (ctx) => {
  if (!voiceOut) {
    voiceOut = ctx.createGain();
    voiceOut.gain.value = 1;
    voiceOut.connect(ctx.destination);
  }
  return voiceOut;
};

const GAP_S = 0.12;
let impulse = null;
const reverb = (ctx) => {
  if (impulse) return impulse;
  const len = Math.floor(ctx.sampleRate * 2.4);
  impulse = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = impulse.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.8);
  }
  return impulse;
};

// One segment; spore segments get reverb, a detuned double and a low, slowed layer.
const startSegment = (ctx, item, when, offset, end, out) => {
  const layer = (detune, gain, dest, delay = 0) => {
    const s = ctx.createBufferSource();
    s.buffer = item.buf;
    s.detune.value = detune;
    const g = ctx.createGain();
    g.gain.value = gain;
    s.connect(g);
    g.connect(dest);
    s.start(when + delay, offset);
    return s;
  };
  if (!item.spore) return [layer(0, 1, out)];
  const verb = ctx.createConvolver();
  verb.buffer = reverb(ctx);
  const wet = ctx.createGain();
  wet.gain.value = 0.6;
  verb.connect(wet);
  wet.connect(out);
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 1200;
  lp.connect(out);
  const main = layer(-60, 0.8, out);
  main.connect(verb);
  const extra = [layer(-25, 0.4, out, 0.03), layer(-500, 0.25, lp)];
  extra.forEach((x) => x.stop(end));
  return [main, ...extra];
};

// Fetches every segment of a reply and returns a playable sequence ({duration, items}).
export const fetchSequence = async (items, opts) => {
  const bufs = await Promise.all(items.map((i) => fetchVoice(i.url, opts)));
  const seq = bufs.map((buf, k) => ({ buf, spore: items[k].spore }));
  return { items: seq, duration: seq.reduce((d, i) => d + i.buf.duration, 0) + GAP_S * (seq.length - 1) };
};

// Plays a decoded buffer (or sequence) through the unlocked AudioContext. Returns stop() or null if blocked.
export const playBuffer = (buf, offset = 0, onEnded) => {
  const ctx = getCtx();
  if (!ctx || ctx.state === "closed" || !buf) return null;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  stopVoice();
  try {
    const items = buf.items || [{ buf, spore: false }];
    const nodes = [];
    let t = ctx.currentTime + 0.02;
    let skip = Math.max(0, offset);
    let last = null;
    for (const it of items) {
      const d = it.buf.duration;
      if (skip >= d - 0.05) { skip = Math.max(0, skip - d - GAP_S); continue; }
      const end = t + d - skip;
      const srcs = startSegment(ctx, it, t, skip, end, output(ctx));
      nodes.push(...srcs);
      last = srcs[0];
      t = end + GAP_S;
      skip = 0;
    }
    const stop = () => {
      nodes.forEach((n) => { n.onended = null; try { n.stop(); } catch (e) { /* not started */ } });
      if (current === stop) current = null;
    };
    if (!last) {
      setTimeout(() => onEnded?.(), 0);
      return stop;
    }
    last.onended = () => {
      if (current === stop) current = null;
      onEnded?.();
    };
    current = stop;
    return stop;
  } catch (e) {
    return null;
  }
};

const stopDevice = () => {
  try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
};

export const stopVoice = () => {
  if (current) current();
  stopDevice();
};

export const primeSpeech = () => {
  try {
    const s = window.speechSynthesis;
    if (!s) return;
    s.getVoices();
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    s.speak(u);
  } catch (e) {
    /* ignore */
  }
};

const pickVoice = () => {
  const vs = window.speechSynthesis.getVoices();
  return (
    vs.find((v) => /^en[-_]GB/i.test(v.lang) && /male|daniel|oliver|arthur|harry/i.test(v.name)) ||
    vs.find((v) => /^en[-_]GB/i.test(v.lang)) ||
    vs.find((v) => /^en/i.test(v.lang))
  );
};

// Device built-in voice backup. Always calls onEnd (capped, since Android may never fire onend).
export const speakDevice = (text, onEnd) => {
  const s = window.speechSynthesis;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(cap);
    onEnd();
  };
  const cap = setTimeout(finish, 3000 + text.length * 110);
  if (fallbackMode !== "device" || !s || !text.trim()) {
    finish();
    return () => {};
  }
  try {
    s.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-GB";
    const v = pickVoice();
    if (v) u.voice = v;
    u.pitch = 1.1;
    u.rate = 1.0;
    u.onend = finish;
    u.onerror = finish;
    s.speak(u);
  } catch (e) {
    finish();
  }
  return () => {
    done = true;
    clearTimeout(cap);
    stopDevice();
  };
};
